import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * RadioCardGroup - A radio group container for card-style radio selections
 * Use with RadioCard components for consistent card-based radio selection UI
 */
const RadioCardGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-3', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioCardGroup.displayName = 'RadioCardGroup';

interface RadioCardProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, 'children'> {
  /** Icon to display at the start of the card */
  icon?: React.ReactNode;
  /** Primary label/title for the option */
  label: React.ReactNode;
  /** Optional description text below the label */
  description?: React.ReactNode;
  /** Additional content to render below description */
  children?: React.ReactNode;
  /** Whether to hide the radio indicator circle */
  hideIndicator?: boolean;
}

/**
 * RadioCard - A card-style radio button where the entire rectangle is clickable
 *
 * @example
 * ```tsx
 * <RadioCardGroup value={value} onValueChange={setValue}>
 *   <RadioCard
 *     value="option1"
 *     icon={<UserIcon className="h-5 w-5" />}
 *     label="Option One"
 *     description="Description for option one"
 *   />
 *   <RadioCard
 *     value="option2"
 *     label="Option Two"
 *     description="Description for option two"
 *   />
 * </RadioCardGroup>
 * ```
 */
const RadioCard = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioCardProps
>(({ className, icon, label, description, children, hideIndicator, value, disabled, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        'group relative flex items-start gap-3 border p-4 rounded-md cursor-pointer transition-colors text-left w-full',
        'hover:bg-muted/50',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary/5',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      {!hideIndicator && (
        <span
          className={cn(
            'flex-shrink-0 mt-0.5 aspect-square h-4 w-4 rounded-full border border-primary',
            'group-data-[state=checked]:bg-primary group-data-[state=checked]:text-primary-foreground',
            'group-disabled:opacity-50'
          )}
        >
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center h-full w-full">
            <Circle className="h-2.5 w-2.5 fill-current text-current" />
          </RadioGroupPrimitive.Indicator>
        </span>
      )}
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          {icon && <span className="text-muted-foreground group-data-[state=checked]:text-primary">{icon}</span>}
          <span className="font-medium">{label}</span>
        </div>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {children}
      </div>
    </RadioGroupPrimitive.Item>
  );
});
RadioCard.displayName = 'RadioCard';

export { RadioCardGroup, RadioCard };
